import tokenRefreshManager from '../../utils/auth/tokenRefreshManager';
import logger from '../../utils/logging/logger';

// Handle a non-ok fetch Response for HttpClient.request(): parse the error body,
// and perform 401 token-refresh-and-retry, 403 CSRF clear-and-retry, session-expiry
// logout/redirect, and email-change reauth. Always either throws (error cases) or
// returns (a retried request result, or undefined for redirect/no-op).
// The client arg is the HttpClient instance (request()'s receiver becomes client.*).
export default async function handleErrorResponse(client, response, endpoint, url, options) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorData = null;
        
        // Clone the response so we can read it multiple times if needed
        const responseClone = response.clone();
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
            
            // Handle email change requiring re-authentication
            if (errorData.requiresReauth) {
              // Check if user is on a quiz page - don't logout during active quiz attempts
              const isOnQuizPage = window.location.pathname.includes('/student/attempt/');
              
              if (isOnQuizPage) {
                // During quiz, don't logout - let the quiz component handle the error
                logger.debug('Email change detected during quiz attempt - not logging out to prevent data loss');
                throw new Error('Email change detected. Please save your answers and refresh the page.');
              }

              logger.info('Email changed, forcing logout and redirect to login');
              // Clear local storage
              localStorage.removeItem('faithinsight_user');
              localStorage.removeItem('faithinsight_token');
              
              // Clear CSRF token
              client.clearCSRFToken();
              
              // Redirect to login page
              window.location.href = '/portal/login';
              return;
            }
            
            // Handle invalid/expired authentication tokens
            if (response.status === 401) {
              // Check if this is a login request - don't treat as token expiration
              // Login 401 errors are just wrong credentials, not expired tokens
              const isLoginRequest = endpoint.includes('/auth/login') || url.includes('/auth/login');

              // Check if this is an OTP verify request - don't treat as token expiration.
              // A wrong/expired one-time code is just a failed login attempt (the error
              // message even says "expired code", which would otherwise falsely match the
              // isTokenExpired check below and force a hard session-expiry redirect).
              const isOtpVerifyRequest = endpoint.includes('/auth/otp/verify') || url.includes('/auth/otp/verify');
              
              // Check if this is a refresh token request - don't treat as session expiration
              // Refresh token failures are handled separately
              const isRefreshRequest = endpoint.includes('/auth/refresh') || url.includes('/auth/refresh');
              
              // Check if this is a signup request - don't treat as token expiration
              // Signup 401 errors are validation errors, not expired tokens
              const isSignupRequest = endpoint.includes('/auth/signup') || url.includes('/auth/signup');
              
              // Check if this is a password reset/forgot password request - don't treat as token expiration
              const isPasswordResetRequest = endpoint.includes('/auth/reset-password') || 
                                            endpoint.includes('/auth/forgot-password') ||
                                            url.includes('/auth/reset-password') ||
                                            url.includes('/auth/forgot-password');
              
              // Check if this is a CSRF token request - don't treat as session expiration
              // CSRF token 401 errors are just missing auth, not expired tokens
              const isCSRFRequest = endpoint.includes('/csrf-token') || url.includes('/csrf-token');
              
              if (isLoginRequest || isOtpVerifyRequest) {
                // For login (password or OTP) requests, don't clear tokens or set session
                // expired flag - just throw the error so the login component can handle it
                // (wrong credentials / wrong code). Prioritize message over error for more
                // user-friendly error display.
                throw new Error(errorData.message || errorData.error || 'Invalid credentials');
              }
              
              if (isRefreshRequest || isSignupRequest || isPasswordResetRequest || isCSRFRequest) {
                // For these requests, don't clear tokens or set session expired flag
                // Just throw the error so the calling component can handle it appropriately
                // Prioritize message over error for more user-friendly error display
                throw new Error(errorData.message || errorData.error || 'Authentication failed');
              }
              
              // Check if this is a token expiration (not other auth errors)
              // Improved detection: check status code first, then error message
              const isTokenExpired = response.status === 401 && (
                errorData.error === 'Token expired' || 
                errorData.error === 'Invalid token' ||
                errorData.message?.includes('expired') ||
                errorData.message?.includes('session has expired') ||
                errorData.message?.includes('Token expired') ||
                errorData.message?.includes('Invalid token')
              );
              
              // Try to refresh token if it's expired
              if (isTokenExpired) {
                try {
                  // Check if refresh is already in progress
                  if (tokenRefreshManager.isRefreshInProgress()) {
                    logger.debug('Token refresh already in progress, waiting...');
                    await tokenRefreshManager.waitForRefresh();
                    // Clear CSRF token after token refresh to prevent user mismatch
                    client.clearCSRFToken();
                    logger.debug('Cleared CSRF token after token refresh (wait path)');
                    // PRIORITY 6: Retry with new token (no timeout for retries)
                    const newToken = localStorage.getItem('faithinsight_token');
                    if (newToken) {
                      const newOptions = {
                        ...options,
                        timeout: null, // No timeout for retry requests (they might take longer)
                        headers: {
                          ...options.headers,
                          'Authorization': `Bearer ${newToken}`,
                          'X-Token-Version': tokenRefreshManager.getTokenVersion().toString()
                        }
                      };
                      return client.request(endpoint, newOptions);
                    }
                  } else {
                    // Perform refresh with locking
                    logger.debug('Attempting to refresh expired access token...');
                    const refreshResult = await tokenRefreshManager.refreshTokenWithLock(client.baseURL);

                    if (refreshResult && refreshResult.token) {
                      logger.info('Token refreshed successfully, retrying original request');

                      // Clear CSRF token after token refresh to prevent user mismatch
                      // CSRF token is tied to user ID, so we need a fresh token after auth token refresh
                      client.clearCSRFToken();
                      logger.debug('Cleared CSRF token after token refresh');

                      // CRITICAL FIX: Fetch new CSRF token before retrying request
                      // This ensures the retry request has a valid CSRF token
                      try {
                        await client.fetchCSRFToken();
                        logger.debug('Fetched new CSRF token after token refresh');
                      } catch (csrfError) {
                        logger.error('Failed to fetch CSRF token after token refresh', { csrfError });
                        // Continue anyway - fetchCSRFToken will be called again in request() method
                        // But it's better to have it ready now
                      }
                      
                      // Check if token version changed during request (stale token)
                      const newTokenVersion = tokenRefreshManager.getTokenVersion();
                      if (client.currentRequestTokenVersion !== null &&
                          client.currentRequestTokenVersion !== newTokenVersion) {
                        logger.debug('Token was refreshed during request, retrying with new token', { oldVersion: client.currentRequestTokenVersion, newVersion: newTokenVersion });
                      }
                      
                      // PRIORITY 6: Retry original request with new token (no timeout for retries)
                      const newOptions = {
                        ...options,
                        timeout: null, // No timeout for retry requests (they might take longer)
                        headers: {
                          ...options.headers,
                          'Authorization': `Bearer ${refreshResult.token}`,
                          'X-Token-Version': newTokenVersion.toString()
                        }
                      };
                      return client.request(endpoint, newOptions);
                    }
                  }
                } catch (refreshError) {
                  logger.error('Token refresh failed', { refreshError, endpoint, url: client.safeLogUrl(url) });
                  
                  if (refreshError.rateLimited) {
                    // Rate limited - wait and retry
                    const error = new Error('Too many refresh attempts. Please wait a moment and try again.');
                    error.rateLimited = true;
                    throw error;
                  }

                  // Any refresh failure other than an expired/missing refresh token is
                  // unexpected - propagate it to the caller.
                  if (!(refreshError.refreshTokenExpired || refreshError.noRefreshToken)) {
                    throw refreshError;
                  }

                  // Refresh token expired or missing: the session cannot be recovered.
                  // Do NOT throw here - fall through to the unified expiry handler below,
                  // which clears tokens, sets the session-expired flag, and redirects to
                  // /login (while still honoring the active-quiz exception). This guarantees
                  // that an expired session navigates to login from any page, not just the
                  // pages that happen to catch this error.
                  logger.warn('Refresh token expired or missing - forcing session expiry', { endpoint, url: client.safeLogUrl(url) });
                }
              }
              
              // Check if user is on a quiz page - don't logout during active quiz attempts
              // This prevents data loss of unsaved quiz answers
              const isOnQuizPage = window.location.pathname.includes('/student/attempt/');
              
              if (isOnQuizPage) {
                // During quiz, don't logout - let the quiz component handle the error
                // This allows answers to be saved before session expires
                logger.debug('Token expired during quiz attempt - not logging out to prevent data loss');
                // Throw error so quiz component can handle it gracefully
                throw new Error('Session expired. Please save your answers and refresh the page.');
              }

              // Check if this is a logout request - don't set session expired flag for deliberate logout
              const isLogoutRequest = endpoint.includes('/auth/logout') || url.includes('/auth/logout');
              
              logger.warn('Invalid or expired authentication token, forcing logout', { status: response.status, errorData, url: client.safeLogUrl(url), isLogoutRequest });
              
              // Clear tokens and force logout for 401 (authentication failure)
              localStorage.removeItem('faithinsight_user');
              localStorage.removeItem('faithinsight_token');
              localStorage.removeItem('faithinsight_refresh_token');
              
              // Clear CSRF token
              client.clearCSRFToken();
              
              if (isLogoutRequest) {
                // For logout requests, set logged out flag (not session expired)
                // This shows "You have been logged out" message instead of "session expired"
                localStorage.setItem('faithinsight_logged_out', 'true');
                // Throw error - let the logout handler in AuthContext handle navigation
                throw new Error('Logout failed: Token expired');
              } else {
                // For non-logout requests, set session expired flag
                localStorage.setItem('faithinsight_session_expired', 'true');
                // Preserve where the user was so we can send them back there after
                // they log in again. Strip the /portal basename (React Router adds it
                // back) and skip auth pages / the root.
                let loginUrl = '/portal/login';
                try {
                  const path = (window.location.pathname || '').replace(/^\/portal/, '') +
                    (window.location.search || '');
                  if (path && !path.startsWith('/login') && path !== '/') {
                    loginUrl += `?redirect=${encodeURIComponent(path)}`;
                  }
                } catch (e) {
                  // Fall back to the bare login URL.
                }
                // Force redirect to login page
                window.location.href = loginUrl;
              }
              return;
            }
            
            // Handle insufficient permissions (403) - don't logout, just throw error
            if (response.status === 403) {
              logger.warn('Insufficient permissions for this resource', { status: response.status, errorData, url: client.safeLogUrl(url) });

              // Check if it's a CSRF-related error
              if (errorData.error === 'CSRF token mismatch' ||
                  errorData.error === 'Invalid CSRF token' ||
                  errorData.error === 'CSRF token expired' ||
                  errorData.error === 'CSRF token missing' ||
                  errorData.message?.includes('CSRF')) {
                logger.debug('CSRF error detected, clearing token and retrying once');
                client.clearCSRFToken();
                
                // Retry the request once with a fresh CSRF token
                const retryCount = options.csrfRetryCount || 0;
                if (retryCount < 1) {
                  try {
                    // Fetch a new CSRF token
                    await client.fetchCSRFToken();
                    // Retry the request with the new token
                    return client.request(endpoint, {
                      ...options,
                      csrfRetryCount: retryCount + 1
                    });
                  } catch (csrfError) {
                    logger.error('Failed to fetch CSRF token for retry', { csrfError });
                    // If CSRF token fetch fails, throw the original error
                  }
                }
              }
              
              // Don't logout for 403 - this is a permission issue, not auth issue
              // Just throw the error to be handled by the calling component
              const error = new Error(errorMessage);
              error.status = response.status;
              error.response = errorData;
              throw error;
            }
          } else {
            // Handle non-JSON responses (like rate limiting or CSRF errors)
            try {
              const textResponse = await responseClone.text();
              // Try to parse as JSON in case content-type header is wrong
              if (textResponse && textResponse.trim().startsWith('{')) {
                try {
                  const parsedJson = JSON.parse(textResponse);
                  errorMessage = parsedJson.message || parsedJson.error || textResponse;
                  errorData = parsedJson;
                } catch (jsonParseError) {
                  // Not valid JSON, use as-is
                  errorMessage = textResponse || errorMessage;
                }
              } else {
                errorMessage = textResponse || errorMessage;
              }
              logger.error('Non-JSON error response', { textResponse, status: response.status, url: client.safeLogUrl(url) });
            } catch (textError) {
              // If we can't read the text either, use a default message based on status
              if (response.status === 403) {
                errorMessage = 'Access denied. You may not have permission to perform this action.';
              } else if (response.status === 429) {
                errorMessage = 'Too many requests. Please wait a moment and try again.';
              } else {
                errorMessage = `HTTP error ${response.status}: ${response.statusText || 'Unknown error'}`;
              }
              logger.error('Could not read error response body', { textError, status: response.status, url: client.safeLogUrl(url) });
            }
          }
        } catch (parseError) {
          logger.error('Could not parse error response', { parseError, status: response.status, url: client.safeLogUrl(url) });
          // Try to get text response as fallback
          try {
            const textResponse = await responseClone.text();
            if (textResponse) {
              // Try to parse as JSON in case content-type header is wrong
              if (textResponse.trim().startsWith('{')) {
                try {
                  const parsedJson = JSON.parse(textResponse);
                  errorMessage = parsedJson.message || parsedJson.error || textResponse;
                  errorData = parsedJson;
                } catch (jsonParseError) {
                  // Not valid JSON, use as-is
                  errorMessage = textResponse;
                }
              } else {
                errorMessage = textResponse;
              }
            }
          } catch (textError) {
            // If we can't read the text either, use a default message based on status
            if (response.status === 403) {
              errorMessage = 'Access denied. You may not have permission to perform this action.';
            } else if (response.status === 429) {
              errorMessage = 'Too many requests. Please wait a moment and try again.';
            } else {
              errorMessage = `HTTP error ${response.status}: ${response.statusText || 'Unknown error'}`;
            }
          }
        }
        
        // Log error details for Apple Pay debugging
        if (endpoint.includes('/confirm-payment')) {
          console.error('🍎 [APPLE PAY] API request failed', {
            url: client.safeLogUrl(url),
            status: response.status,
            statusText: response.statusText,
            errorMessage,
            errorData,
            timestamp: new Date().toISOString()
          });
        }
        
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = errorData;
        throw error;
}
